import React from "react";

import useSWR from "swr";

import EngineeringIcon from "@mui/icons-material/Engineering";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import Widgets from "@mui/icons-material/Widgets";

import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { swrFetcher } from "../axiosClient.js";
import PageContext from "../contexts/PageContext.jsx";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import Alert from "./common/Alert.jsx";
import NavButton from "./common/NavButton.jsx";

function SideBar({
  // Props
  onClick,
}) {
  const { PageButtons } = React.useContext(PageContext);

  const { data: versionData, error: swrError, mutate } = useSWR({ url: "/api/version" }, swrFetcher);

  const version = versionData?.version || "";
  const error = swrError ? swrError.response?.data?.error || swrError.message : null;
  const setError = React.useCallback(
    (val) => {
      if (val === null) {
        mutate();
      }
    },
    [mutate]
  );

  const { roles } = useAuth0Roles();

  const hasTZ = React.useMemo(() => roles.includes("Scorecard TZ Medical Viewer"), [roles]);
  const hasTZMMS = React.useMemo(() => roles.includes("Scorecard TZMMS Viewer"), [roles]);
  const hasENGR = React.useMemo(() => roles.includes("Scorecard Engineering Viewer"), [roles]);
  const hasPLC = React.useMemo(() => roles.includes("Scorecard PLC Viewer"), [roles]);

  const theme = useTheme();

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      <List onClick={onClick}>
        {!!PageButtons && (
          <>
            {PageButtons}
            <Divider color={theme.palette.primary.dark} sx={{ borderWidth: "1px" }} />
          </>
        )}
        {hasTZ && <NavButton Icon={<Widgets />} text="TZ Medical" to="/tz-medical" />}
        {hasTZMMS && <NavButton Icon={<MonitorHeartIcon />} text="TZMMS" to="/tzmms" />}
        {hasENGR && <NavButton Icon={<SettingsApplicationsIcon />} text="Engineering" to="/engineering" />}
        {(hasENGR || hasPLC) && <Divider color={theme.palette.primary.dark} sx={{ borderWidth: "1px" }} />}
        {hasPLC && (
          <>
            <NavButton Icon={<RocketLaunchIcon />} text="PLCs" to="/product-launch-checklists" />
          </>
        )}
        {hasENGR && (
          <>
            <NavButton Icon={<ManageHistoryIcon />} text="Engineering Projects" to="/engineering-projects" />
            <Divider color={theme.palette.primary.dark} sx={{ borderWidth: "1px" }} />
            <NavButton Icon={<EngineeringIcon />} text="Engineers" to="/engineers" />
          </>
        )}
      </List>
      <Typography
        data-cy="version-number"
        sx={{
          paddingLeft: 2,
          position: "absolute",
          bottom: 0,
          paddingBottom: 1,
          fontSize: "0.75rem",
          opacity: 0.7,
        }}
      >
        v{version}
      </Typography>
      <Alert message={error} setMessage={setError} level="error" variant="snackbar" />
    </>
  );
}

export default SideBar;
